/**
 * NORMALIZE IMAGE URL — Ensures Azure Blob URLs have extensions and are cleaned.
 */
export function normalizeImageUrl(url: any): string {
  if (!url || typeof url !== 'string' || url.trim() === '') return '';

  let resolved = url.trim();

  // 1. Try to extract embedded blob URL (handles the broken photoUrl pattern)
  // Updated regex to allow spaces in filenames
  const blobMatch = url.match(/https:\/\/[a-zA-Z0-9.-]+\.blob\.core\.windows\.net\/[^"\\}]+/);
  if (blobMatch) {
    resolved = blobMatch[0].trim();
  }

  // 2. Normalization: Ensure extension exists for Azure Blob URLs
  // Backend sometimes sends URLs without extensions (e.g. .../medium_123)
  if (
    resolved.includes('.blob.core.windows.net') &&
    !resolved.match(/\.(jpg|jpeg|png|webp|gif|svg|mp4|mov)$/i)
  ) {
    resolved = `${resolved}.jpg`;
  }

  return resolved;
}

/**
 * CLEAN PHOTO URL — Backward compatibility / internal helper
 */
export function cleanUrl(url: any): string {
  return normalizeImageUrl(url);
}

/**
 * Resolves a product image based on requested size with intelligent fallbacks.
 */
export function resolveProductImage(
  product: any,
  size: 'thumbnail' | 'medium' | 'original' = 'original',
): string {
  if (!product) return '/assets/no-image.png';

  // 1. PRIORITY: Explicit variant properties from Mappers (photoThumbnailUrl, photoMediumUrl, photoUrl)
  if (size === 'thumbnail' && product.photoThumbnailUrl)
    return normalizeImageUrl(product.photoThumbnailUrl);
  if (size === 'medium' && product.photoMediumUrl) return normalizeImageUrl(product.photoMediumUrl);
  if (product.photoUrl) return normalizeImageUrl(product.photoUrl);

  // 2. PRIORITY: New API nested objects (boatThumbnailPhoto, merchandiseThumbnailPhoto)
  const tp = product.boatThumbnailPhoto || product.merchandiseThumbnailPhoto;
  if (tp) {
    if (size === 'thumbnail' && tp.photoThumbnailUrl)
      return normalizeImageUrl(tp.photoThumbnailUrl);
    if (size === 'medium' && tp.photoMediumUrl) return normalizeImageUrl(tp.photoMediumUrl);
    if (tp.photoUrl) return normalizeImageUrl(tp.photoUrl);
  }

  // 3. PRIORITY: Nested Media Object summary
  if (product.media) {
    if (size === 'thumbnail' && product.media.photoThumbnailUrl)
      return normalizeImageUrl(product.media.photoThumbnailUrl);
    if (size === 'medium' && product.media.photoMediumUrl)
      return normalizeImageUrl(product.media.photoMediumUrl);
    if (product.media.photoUrl) return normalizeImageUrl(product.media.photoUrl);
  }

  // 4. FALLBACK: Root-level common fields
  const root = product.boat || product.merchandise || product;
  const rootPhoto = root.photoUrl || root.thumbnailUrl || root.mainPhoto;
  const rootThumb = root.photoThumbnailUrl || root.thumbnailPhotoUrl;

  if (size === 'thumbnail')
    return normalizeImageUrl(rootThumb || rootPhoto || '/assets/no-image.png');
  return normalizeImageUrl(rootPhoto || rootThumb || '/assets/no-image.png');
}
